import dayjs from 'dayjs'
import { useMemo, useState } from 'react'

// Curie design-system components
import { Button, Tag, Typography } from '~/components'
import { CopySnippet } from '~/components/common/CopySnippet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { List } from '~/components/common/List'
import { Modal, ModalBody, ModalHeaderContent } from '~/components/modals/CurieModal'

import { FiChevronDown } from '@react-icons/all-files/fi/FiChevronDown'
import { FiKey } from '@react-icons/all-files/fi/FiKey'
import { FiPlus } from '@react-icons/all-files/fi/FiPlus'

// Data hooks
import {
  useApiKeys,
  useDeleteApiKey,
  useGenerateApiKey,
  useRevokeApiKey
} from '~/services/apiKeysService'

import { useQueryClient } from '@tanstack/react-query'
import { BetterToast } from '~/utils/toast'

const BASE_URL = 'https://platform.curietech.ai'
const MCP_SETUP_GUIDE_URL = 'https://docs.curietech.ai/docs/SecurityAndUserManagement/MCPSetup'

export default function ApiKeysSettingsTab() {
  const queryClient = useQueryClient()
  const [isApiKeyExpanded, setIsApiKeyExpanded] = useState(false)
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false)

  const generateKeyMutation = useGenerateApiKey()
  const { data: apiKeys = [], isLoading, error } = useApiKeys()

  const revokeKeyMutation = useRevokeApiKey()
  const deleteKeyMutation = useDeleteApiKey()

  const isRevokePending = revokeKeyMutation.isPending
  const isDeletePending = deleteKeyMutation.isPending

  const hasKeys = apiKeys.length > 0

  const endpoint = `${BASE_URL}/api/code-review/run`
  const curlExample = `curl -X POST "${endpoint}" \\
  -H "Authorization: Bearer $CURIE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "conn_type": "One of: github/bitbucket/gitlab/azure_devops",
    "org_name": "Name of the organization",
    "repo_name": "Name of the repository without the .git suffix",
    "branch_name": "Name of the feature branch",
    "pr_number": [Integer] PR number,
    "prompt": "[Optional] Any additional instructions for the code review agent"
  }'`

  const mcpCommand = `claude mcp add --transport http curie-dataweave ${BASE_URL}/mcp/ --scope user`

  // Column definitions for shared List component
  const columns = useMemo(() => {
    return [
      { label: 'API Key', flex: 2, align: 'left' as const },
      { label: 'Created At', flex: 1, align: 'left' as const },
      { label: 'Last Used At', flex: 1, align: 'left' as const },
      { label: 'Status', width: '120px', align: 'center' as const },
      { label: 'Actions', width: '60px', align: 'center' as const }
    ]
  }, [])

  const values = useMemo(() => {
    return apiKeys.map(key => [
      // Token
      <div
        key={`token-${key.id}`}
        className='truncate max-w-[220px]'
        style={{
          color: 'var(--neutral-text-primary, rgba(0, 0, 0, 0.88))',
          fontFamily: 'Geist',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 'bold',
          lineHeight: '18px'
        }}
      >
        {key.truncated_token}
      </div>,
      // Created At
      <Typography
        key={`created-${key.id}`}
        fontFamily='geist'
        fontSize='bodySm'
        fontWeight='medium'
        className='text-tokens-neutral-text-secondary'
      >
        {dayjs.unix(key.created_at_unix).format('MMM D, YYYY')}
      </Typography>,
      // Last Used At
      <Typography
        key={`used-${key.id}`}
        fontFamily='geist'
        fontSize='bodySm'
        fontWeight='medium'
        className='text-tokens-neutral-text-secondary'
      >
        {(() => {
          if (!key.last_used_at_unix) {
            return 'Never used'
          }

          const lastUsed = dayjs.unix(key.last_used_at_unix)
          const thirtyDaysAgo = dayjs().subtract(30, 'days')

          if (lastUsed.isBefore(thirtyDaysAgo)) {
            return 'Over 30 days ago'
          }

          return lastUsed.format('MMM D, YYYY')
        })()}
      </Typography>,
      // Status tag
      <Tag
        key={`status-${key.id}`}
        variant={key.revoked ? 'error' : 'success'}
        className='mx-auto select-none'
      >
        {key.revoked ? 'REVOKED' : 'ACTIVE'}
      </Tag>,
      // Actions dropdown
      <DropdownMenu key={`actions-${key.id}`}>
        <DropdownMenuTrigger>
          <Button
            variant='borderless'
            className='flex w-[36px] h-[36px] p-0 hover:rounded-[8px]'
            aria-label='API key actions menu'
          >
            {/* Ellipsis icon */}
            <svg
              width='36'
              height='36'
              viewBox='0 0 36 36'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M11.333 18.2604C11.333 18.404 11.3613 18.5463 11.4163 18.679C11.4712 18.8117 11.5518 18.9322 11.6534 19.0338C11.7549 19.1354 11.8755 19.2159 12.0082 19.2709C12.1409 19.3259 12.2831 19.3542 12.4268 19.3542C12.5704 19.3542 12.7126 19.3259 12.8453 19.2709C12.978 19.2159 13.0986 19.1354 13.2002 19.0338C13.3017 18.9322 13.3823 18.8117 13.4373 18.679C13.4922 18.5463 13.5205 18.404 13.5205 18.2604C13.5205 18.1168 13.4922 17.9745 13.4373 17.8418C13.3823 17.7091 13.3017 17.5886 13.2002 17.487C13.0986 17.3854 12.978 17.3049 12.8453 17.2499C12.7126 17.1949 12.5704 17.1667 12.4268 17.1667C12.2831 17.1667 12.1409 17.1949 12.0082 17.2499C11.8755 17.3049 11.7549 17.3854 11.6534 17.487C11.5518 17.5886 11.4712 17.7091 11.4163 17.8418C11.3613 17.9745 11.333 18.1168 11.333 18.2604ZM16.8018 18.2604C16.8018 18.5505 16.917 18.8287 17.1221 19.0338C17.3272 19.2389 17.6054 19.3542 17.8955 19.3542C18.1856 19.3542 18.4638 19.2389 18.6689 19.0338C18.874 18.8287 18.9893 18.5505 18.9893 18.2604C18.9893 17.9703 18.874 17.6921 18.6689 17.487C18.4638 17.2819 18.1856 17.1667 17.8955 17.1667C17.6054 17.1667 17.3272 17.2819 17.1221 17.487C16.917 17.6921 16.8018 17.9703 16.8018 18.2604ZM22.2705 18.2604C22.2705 18.5505 22.3857 18.8287 22.5909 19.0338C22.796 19.2389 23.0742 19.3542 23.3643 19.3542C23.6543 19.3542 23.9325 19.2389 24.1377 19.0338C24.3428 18.8287 24.458 18.5505 24.458 18.2604C24.458 17.9703 24.3428 17.6921 24.1377 17.487C23.9325 17.2819 23.6543 17.1667 23.3643 17.1667C23.0742 17.1667 22.796 17.2819 22.5909 17.487C22.3857 17.6921 22.2705 17.9703 22.2705 18.2604Z'
                fill='black'
                fillOpacity='0.45'
              />
            </svg>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem
            className='select-none'
            disabled={key.revoked || isRevokePending}
            onClick={() => {
              revokeKeyMutation.mutate(
                { tokenId: key.id },
                {
                  onSuccess: () => {
                    BetterToast.success('API key disabled')
                    queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
                  },
                  onError: (err: any) => BetterToast.error(err.message || 'Failed to disable')
                }
              )
            }}
          >
            Disable API Key
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className='select-none text-tokens-status-error-text'
            onClick={() => {
              deleteKeyMutation.mutate(
                { tokenId: key.id },
                {
                  onSuccess: () => {
                    BetterToast.success('API key deleted')
                    queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
                  },
                  onError: (err: any) => BetterToast.error(err.message || 'Failed to delete')
                }
              )
            }}
            disabled={isDeletePending}
          >
            {isDeletePending ? 'Deleting...' : 'Delete API Key'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ])
  }, [apiKeys, isRevokePending, isDeletePending, queryClient])

  const handleGenerateKey = () => {
    generateKeyMutation.mutate(
      { expiresMinutes: undefined },
      {
        onSuccess: () => {
          BetterToast.success('Generated a new API key')
          setIsApiKeyExpanded(true)
          queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
        },
        onError: (err: any) => BetterToast.error(err.message || 'Failed to generate')
      }
    )
  }

  if (isLoading) {
    return <div className='select-none'>Loading API keys...</div>
  }

  if (error) {
    return <div>Error loading API keys: {(error as Error).message}</div>
  }

  return (
    <div className='w-full h-full overflow-auto no-scrollbar'>
      {hasKeys && (
        /* Header band – mirrors Runner list header style */
        <div className='bg-tokens-neutral-fill-fill-4 rounded-[16px] h-[68px] mb-6 w-full mt-12 justify-between p-4'>
          <div className='flex flex-row items-center justify-between'>
            <div className='flex flex-row items-center flex-1 gap-3'>
              {/* API Key icon in coloured circle */}
              <div className='bg-tokens-status-link-base rounded-full p-1 w-6 h-6 flex items-center justify-center'>
                <FiKey className='w-3.5 h-3.5 text-black' />
              </div>
              <Typography
                fontFamily='geistMono'
                fontSize='labelSm'
                fontWeight='semibold'
                className='text-tokens-neutral-text-tertiary flex-1 select-none'
              >
                Add new API keys to enable integration and secure access.
              </Typography>
            </div>
            <Button
              onClick={handleGenerateKey}
              aria-label='Add API key button'
              variant='outlined'
              leftIcon={<FiPlus className='w-4 h-4' />}
              isDisabled={generateKeyMutation.isPending}
              className='ml-auto select-none'
            >
              Add API Key
            </Button>
            <Button
              onClick={() => setIsInstructionsOpen(true)}
              variant='borderless'
              className='ml-2 p-2'
              aria-label='View API instructions'
            >
              <svg
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M4 19.5C4 18.1193 5.11929 17 6.5 17H20'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M6.5 2H20V22H6.5C5.11929 22 4 20.8807 4 19.5V4.5C4 3.11929 5.11929 2 6.5 2Z'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M8 7H16'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M8 11H16'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </Button>
          </div>
        </div>
      )}

      {generateKeyMutation.data && (
        <div className='mb-6 border rounded-[16px] bg-tokens-neutral-fill-fill-4 overflow-hidden'>
          <button
            onClick={() => setIsApiKeyExpanded(!isApiKeyExpanded)}
            className='w-full p-4 flex items-center justify-between hover:bg-tokens-neutral-fill-fill-3 transition-colors'
          >
            <div className='flex items-center gap-3'>
              <div className='bg-tokens-status-success-base rounded-full p-1 w-6 h-6 flex items-center justify-center'>
                <FiKey className='w-3.5 h-3.5 text-white' />
              </div>
              <div className='text-left'>
                <Typography
                  fontFamily='geist'
                  fontSize='labelSm'
                  fontWeight='bold'
                  className='block select-none'
                >
                  New API Key Generated
                </Typography>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='text-tokens-neutral-text-secondary select-none'
                >
                  Copy now – shown once only!
                </Typography>
              </div>
            </div>
            <FiChevronDown
              className={`w-4 h-4 transition-transform ${isApiKeyExpanded ? 'rotate-180' : ''}`}
            />
          </button>

          {isApiKeyExpanded && (
            <div className='px-4 pb-4 border-t border-tokens-neutral-stroke-stroke-2'>
              <div className='mt-4'>
                <Typography
                  fontFamily='geist'
                  fontSize='labelSm'
                  fontWeight='medium'
                  className='text-tokens-neutral-text-secondary mb-2 select-none'
                >
                  API Key
                </Typography>
                <CopySnippet
                  text='Copy API key to clipboard'
                  code={generateKeyMutation.data.token}
                  isSecret={false}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions Modal */}
      <Modal
        isOpen={isInstructionsOpen}
        onClose={() => setIsInstructionsOpen(false)}
        hideCloseButton={false}
      >
        <ModalBody>
          <ModalHeaderContent title='API Instructions' />

          <div className='mt-8'>
            <Typography
              fontFamily='geist'
              fontSize='titleSm'
              fontWeight='bold'
              className='text-tokens-neutral-text-primary block mb-3 select-none'
            >
              Connect to Claude Code
            </Typography>
            <div className='space-y-2 mb-4'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                className='text-tokens-neutral-text-secondary select-none'
              >
                Step 1: Generate an API key in Curie
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                className='text-tokens-neutral-text-secondary select-none'
              >
                Step 2: Run the command below in your Claude Code terminal
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                className='text-tokens-neutral-text-secondary select-none'
              >
                Step 3: Complete the browser authorization prompt by entering your Curie API key
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                className='text-tokens-neutral-text-secondary select-none'
              >
                Step 4: Once connected, invoke CurieTech AI in Claude Code with your prompt
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='text-tokens-neutral-text-tertiary italic pl-4 select-none'
              >
                Example: "Use CurieTech AI MCP server to …"
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='text-tokens-neutral-text-secondary select-none'
              >
                Need help?{' '}
                <a
                  href={MCP_SETUP_GUIDE_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='font-semibold text-tokens-status-link-3 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tokens-status-link-2 focus-visible:ring-offset-2'
                >
                  Open the MCP setup guide
                </a>
                .
              </Typography>
            </div>
            <CopySnippet text='Copy MCP command' code={mcpCommand} isSecret={false} />
          </div>

          <div className='my-6 border-t border-tokens-neutral-stroke-stroke-2' />

          <div>
            <Typography
              fontFamily='geist'
              fontSize='titleSm'
              fontWeight='bold'
              className='text-tokens-neutral-text-primary block mb-3 select-none'
            >
              Automated Code Review
            </Typography>
            <div className='space-y-2 mb-4'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                className='text-tokens-neutral-text-secondary select-none'
              >
                Step 1: Configure your repository on the Curie platform (Settings &gt; Repositories
                &gt; Connect)
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                className='text-tokens-neutral-text-secondary select-none'
              >
                Step 2: Generate an API key (Settings &gt; API Keys)
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                className='text-tokens-neutral-text-secondary select-none'
              >
                Step 3: Add the provided cURL command to your CI/CD workflow (e.g. in a GitHub
                Actions workflow)
              </Typography>
            </div>
            <CopySnippet text='Copy cURL command' code={curlExample} isSecret={false} />
          </div>
        </ModalBody>
      </Modal>

      {hasKeys ? (
        <List columns={columns} values={values} />
      ) : (
        <div className='flex flex-col items-center justify-center py-16'>
          <div className='w-24 h-24 flex items-center justify-center mb-6'>
            {/* Reuse the blank-state grid icon */}
            <svg
              width='96'
              height='96'
              viewBox='0 0 96 96'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M80 8H16C11.5817 8 8 11.5817 8 16V32C8 36.4183 11.5817 40 16 40H80C84.4183 40 88 36.4183 88 32V16C88 11.5817 84.4183 8 80 8Z'
                stroke='#262626'
                strokeOpacity='0.2'
                strokeWidth='8'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M80 56H16C11.5817 56 8 59.5817 8 64V80C8 84.4183 11.5817 88 16 88H80C84.4183 88 88 84.4183 88 80V64C88 59.5817 84.4183 56 80 56Z'
                stroke='#262626'
                strokeOpacity='0.2'
                strokeWidth='8'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M24 24H24.04'
                stroke='#262626'
                strokeOpacity='0.2'
                strokeWidth='8'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M24 72H24.04'
                stroke='#262626'
                strokeOpacity='0.2'
                strokeWidth='8'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='text-tokens-neutral-text-tertiary text-center max-w-md mb-4 select-none'
          >
            You haven't added any API keys. Add new ones to enable integration and secure access.
          </Typography>
          <div className='flex gap-4'>
            <Button
              variant='outlined'
              leftIcon={<FiPlus className='w-4 h-4' />}
              onClick={handleGenerateKey}
              className='select-none'
            >
              Add API Keys
            </Button>
            <button
              className='text-tokens-neutral-text-primary font-semibold underline text-sm select-none'
              onClick={() => setIsInstructionsOpen(true)}
              aria-label='View API instructions'
            >
              View Instructions
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
